import { memo, useContext } from 'react';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';
import { T } from '../../../translation/src';
import { AnnotationType } from '../../../modules/Properties/types';
import { PropertiesSection, Property } from './PropertiesSection';
import { RendererContext } from '../../../contexts/RendererContext';
import { useRoomProperties } from '../../../modules/Properties/integration/AnnotationPropertyIntegration';
import { BaseElevationInput, CeilingElevationInput } from '../../../modules/Properties/integration/annotations/RoomAnnotationProperties';
import { PropertyPanel } from '../../../modules/Properties/components/PropertyPanel';
import { getRoomPolygonProperty } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';

interface RoomPropertiesProps {
  identifier: string;
  projectId: string;
}

const RoomProperties_: React.FC2<RoomPropertiesProps> = ({ identifier, projectId }) => {
  const { formatNumber } = useFormatNumber();

  // Get property values for calculations
  const { getBaseElevation, getCeilingElevation } = useRoomProperties(projectId);
  const baseElevation = getBaseElevation(identifier);
  const ceilingElevation = getCeilingElevation(identifier);

  const renderContext = useContext(RendererContext);
  const viewer = renderContext.viewer;

  // Rooms are stored in viewer.scene.rooms, not measurements
  const roomObject = viewer?.scene.rooms?.find((r) => r.identifier === identifier);

  // Get room measurements from the PointoramaRoom object
  const perimeter = roomObject?.getPerimeter() || 0;
  const baseArea = roomObject?.getArea() || 0;
  const volume = roomObject?.getVolume() || 0;
  const height = Math.abs(ceilingElevation - baseElevation);

  // Count number of walls (number of polygon edges)
  const polygon = usePropertyValue(getRoomPolygonProperty(identifier));
  const numberOfWalls = polygon.length;

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          <T _str="room as-built" swc />
        </Property>
        <Property title={<T _str="number of walls" swc />}>{numberOfWalls}</Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={<T _str="perimeter" swc />}>{formatNumber(perimeter, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
        <Property title={<T _str="area" swc />}>{formatNumber(baseArea, { convertType: ConvertType.AREA, addUnit: true })}</Property>
        <Property title={<T _str="volume" swc />}>{formatNumber(volume, { convertType: ConvertType.VOLUME, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="dimensions" swc />}>
        <Property title={<T _str="base elevation" swc />}>
          <BaseElevationInput roomId={identifier} projectId={projectId} />
        </Property>
        <Property title={<T _str="ceiling elevation" swc />}>
          <CeilingElevationInput roomId={identifier} projectId={projectId} />
        </Property>
        <Property title={<T _str="room height" swc />}>{formatNumber(height, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertyPanel projectId={projectId} annotationTypes={[AnnotationType.ROOM]} objectIdentifiers={[identifier]} />
    </div>
  );
};

export const RoomProperties = memo(RoomProperties_);
